import { io, Socket } from 'socket.io-client';

/**
 * 🔧 Helper: ดึง Socket URL แบบ dynamic รองรับหลาย domain
 * ลำดับความสำคัญ:
 * 1. REACT_APP_API_URL (ถ้ามี)
 * 2. สร้างจาก current domain (เช่น merchant.gm2pay.com → api.gm2pay.com)
 * 3. localhost:5000 (development)
 */
function getSocketUrl(): string {
  const webName = process.env.REACT_APP_WEB_NAME || 'default';

  // 1. ถ้ามี env variable ให้ใช้ก่อน
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL.replace('/api', '');
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return `${url}/${webName}`;
    }
  }

  // 2. สร้างจาก current domain (production)
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    const hostname = window.location.hostname;
    // merchant.gm2pay.com → api.gm2pay.com
    // manage.qpay168.cloud → api.qpay168.cloud
    const apiHost = hostname.replace(/^(merchant|manage|admin)\./, 'api.');
    return `${window.location.protocol}//${apiHost}/${webName}`;
  }

  // 3. Development fallback
  return `http://localhost:5000/${webName}`;
}

class SocketService {
  private socket: Socket | null = null;
  private merchantId: string | null = null;
  // ✅ Queue สำหรับเก็บ listeners ที่ลงทะเบียนก่อน socket connect
  private pendingListeners: Map<string, Set<(...args: any[]) => void>> = new Map();

  connect(merchantId: string) {
    // ถ้า socket มีอยู่แล้วและ merchantId เดิม
    if (this.socket && this.merchantId === merchantId) {
      if (this.socket.connected) {
        return;
      }
      // ถ้ากำลัง connecting อยู่ (ยังไม่ disconnected) ให้รอ
      if (!this.socket.disconnected) {
        console.log('🔌 Socket already connecting...');
        return;
      }
    }

    // ถ้าเปลี่ยน merchantId ให้ disconnect ก่อน
    if (this.socket && this.merchantId !== merchantId) {
      this.disconnect();
    }

    const socketUrl = getSocketUrl();
    console.log('🔌 Connecting to Socket.IO (Merchant):', socketUrl);
    
    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      timeout: 20000
    });

    this.merchantId = merchantId;

    this.socket.on('connect', () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 Socket connected:', this.socket?.id);
      }
      
      // ✅ ส่ง merchantId อย่างเดียว - backend จะอ่าน cookie เอง
      this.socket?.emit('join-merchant', { merchantId });
      
      // ✅ ลงทะเบียน pending listeners
      this.registerPendingListeners();
    });

    this.socket.on('disconnect', () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔌 Socket disconnected');
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });

    // รับ event จาก backend
    this.socket.on('joined', (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Joined room:', data.room);
      }
    });

    this.socket.on('error', (error) => {
      console.error('❌ Socket error:', error.message);
    });
  }

  // ⭐ NEW - Connect สำหรับ Admin (join admin room)
  connectAdmin() {
    // ถ้า socket มีอยู่แล้ว (connected หรือ connecting) ไม่ต้องสร้างใหม่
    if (this.socket) {
      if (this.socket.connected) {
        console.log('🔌 Admin Socket already connected');
        return;
      }
      // ถ้ากำลัง connecting อยู่ (ยังไม่ disconnected) ให้รอ
      if (!this.socket.disconnected) {
        console.log('🔌 Admin Socket already connecting...');
        return;
      }
    }

    const socketUrl = getSocketUrl();
    console.log('🔌 Connecting to Socket.IO (Admin):', socketUrl);
    
    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      timeout: 20000
    });

    this.socket.on('connect', () => {
      console.log('🔌 Admin Socket connected:', this.socket?.id);
      // 🟢 Join admin room เพื่อ track presence
      this.socket?.emit('join-admin');
      // 🔄 Start heartbeat ทุก 30 วินาที
      this.startHeartbeat();
      // ✅ ลงทะเบียน pending listeners
      this.registerPendingListeners();
    });

    this.socket.on('joined', (data) => {
      console.log('✅ Admin joined room:', data.room);
    });

    this.socket.on('force-disconnect', (data) => {
      console.log('🔴 Force disconnected by admin:', data.message);
      alert('คุณถูก disconnect จากระบบโดย Admin');
      window.location.href = '/admin/login';
    });

    this.socket.on('disconnect', () => {
      console.log('🔌 Admin Socket disconnected');
      this.stopHeartbeat();
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });
  }

  private heartbeatInterval: NodeJS.Timeout | null = null;

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('heartbeat');
      }
    }, 30000); // ทุก 30 วินาที
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.socket) {
      // ✅ เช็คว่า connected แล้วค่อย disconnect เพื่อหลีกเลี่ยง warning ใน React Strict Mode
      if (this.socket.connected) {
        this.socket.disconnect();
      } else {
        // ถ้ายังไม่ connected ให้ลบ listeners และ set null
        this.socket.removeAllListeners();
        this.socket.close();
      }
      this.socket = null;
      this.merchantId = null;
    }
  }

  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      console.log(`🔔 Registering listener for: ${event}`);
      this.socket.on(event, callback);
    } else {
      // ถ้า socket ยังไม่มี ให้เก็บไว้ใน pending queue
      console.log(`⏳ Socket not ready, queuing listener for: ${event}`);
      if (!this.pendingListeners.has(event)) {
        this.pendingListeners.set(event, new Set());
      }
      this.pendingListeners.get(event)?.add(callback);
    }
  }

  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
    // ลบจาก pending queue ด้วย
    if (callback && this.pendingListeners.has(event)) {
      this.pendingListeners.get(event)?.delete(callback);
    }
  }

  // ✅ เรียกเมื่อ socket connected เพื่อลงทะเบียน pending listeners
  private registerPendingListeners() {
    if (!this.socket) return;
    
    this.pendingListeners.forEach((callbacks, event) => {
      callbacks.forEach(callback => {
        console.log(`🔔 Registering queued listener for: ${event}`);
        this.socket?.on(event, callback);
      });
    });
    // Clear pending queue
    this.pendingListeners.clear();
  }

  emit(event: string, data: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export default new SocketService();

